/**
 * Pure classification of a CRM field into an editor kind and its commit
 * semantics. Reproduces the decision logic embedded in
 * `lib/base/table/components/editable-cell.tsx` (isBareTextEditor,
 * isPhoneEditor, isPickerEditor, isDateEditor, isPlainDateEditor,
 * isMarkdownEditor, isSingleAssociation, isCardEditor, isPhoneCardEditor,
 * and the enter-key/commit-on-pick/layout derivations) so it can be
 * unit-tested independently of React and the table component.
 */

import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

export type EditorKind =
  | 'bare-text'
  | 'phone'
  | 'picker'
  | 'date'
  | 'datetime'
  | 'markdown'
  | 'textarea'
  | 'single-association'
  | 'multi-association'
  | 'multi-value-card'
  | 'composite-multi'
  | 'generic';

export type CommitTrigger = 'enter-or-blur' | 'pick' | 'blur-only';

export interface EditorKindDescriptor {
  kind: EditorKind;
  commitTrigger: CommitTrigger;
  /**
   * True when the editor consumes the Enter key itself (new line, add-value,
   * pick from listbox, etc.) — the cell must not intercept Enter to commit.
   */
  ownsEnterKey: boolean;
  /** True when PropertyInput should auto-open its popover on mount. */
  autoOpenPicker: boolean;
  /** True when the association picker should render pre-opened. */
  defaultOpen: boolean;
  overlayLayout: 'row' | 'card' | 'card-flush';
}

// Single-line editors that render a bare <input> — moved verbatim from
// editable-cell.tsx.
export const BARE_INPUT_FIELD_TYPES = new Set([
  'text',
  'email',
  'domain',
  'url',
  'number',
  'percentage',
  'currency',
]);

export interface EditorKindClassifyInput {
  propertyType?: PropertyTypeSchema;
  association?: { associationType: Pick<AssociationType, 'cardinality'> };
}

/**
 * Classify a CRM field into its editor kind and commit semantics, matching
 * editable-cell.tsx's behaviour exactly. Decision order (first match wins)
 * mirrors the derivations at editable-cell.tsx:334-396: association
 * cardinality, then is_multi (with/without subproperties), then field type.
 */
export function classifyEditorKind({
  propertyType,
  association,
}: EditorKindClassifyInput): EditorKindDescriptor {
  if (association) {
    const isSingleAssociation =
      association.associationType.cardinality === 'many_to_one' ||
      association.associationType.cardinality === 'one_to_one';
    if (isSingleAssociation) {
      return {
        kind: 'single-association',
        commitTrigger: 'pick',
        ownsEnterKey: true,
        autoOpenPicker: false,
        defaultOpen: false,
        overlayLayout: 'card-flush',
      };
    }
    return {
      kind: 'multi-association',
      commitTrigger: 'blur-only',
      ownsEnterKey: true,
      autoOpenPicker: false,
      defaultOpen: true,
      overlayLayout: 'card',
    };
  }

  if (!propertyType) {
    return {
      kind: 'generic',
      commitTrigger: 'enter-or-blur',
      ownsEnterKey: false,
      autoOpenPicker: false,
      defaultOpen: false,
      overlayLayout: 'row',
    };
  }

  if (propertyType.is_multi) {
    if (propertyType.subproperties?.length) {
      return {
        kind: 'composite-multi',
        commitTrigger: 'blur-only',
        ownsEnterKey: true,
        autoOpenPicker: false,
        defaultOpen: false,
        overlayLayout: 'row',
      };
    }
    return {
      kind: 'multi-value-card',
      commitTrigger: 'blur-only',
      ownsEnterKey: true,
      autoOpenPicker: false,
      defaultOpen: false,
      overlayLayout: 'card',
    };
  }

  if (propertyType.field_type_name === 'markdown') {
    return {
      kind: 'markdown',
      commitTrigger: 'blur-only',
      ownsEnterKey: true,
      autoOpenPicker: false,
      defaultOpen: false,
      overlayLayout: 'card-flush',
    };
  }

  if (propertyType.field_type_name === 'textarea') {
    return {
      kind: 'textarea',
      commitTrigger: 'blur-only',
      ownsEnterKey: true,
      autoOpenPicker: false,
      defaultOpen: false,
      overlayLayout: 'row',
    };
  }

  if (propertyType.field_type_name === 'date') {
    return {
      kind: 'date',
      commitTrigger: 'pick',
      ownsEnterKey: false,
      autoOpenPicker: true,
      defaultOpen: false,
      overlayLayout: 'row',
    };
  }

  if (propertyType.field_type_name === 'datetime') {
    // date + time is a two-step pick in one popover — committing after step
    // one would save a half-finished value, so datetime commits on blur
    // instead of pick (editable-cell.tsx:355-358).
    return {
      kind: 'datetime',
      commitTrigger: 'blur-only',
      ownsEnterKey: false,
      autoOpenPicker: true,
      defaultOpen: false,
      overlayLayout: 'row',
    };
  }

  if (
    propertyType.field_type_name === 'select' ||
    propertyType.field_type_name === 'boolean'
  ) {
    return {
      kind: 'picker',
      commitTrigger: 'pick',
      ownsEnterKey: false,
      autoOpenPicker: true,
      defaultOpen: false,
      overlayLayout: 'row',
    };
  }

  if (propertyType.field_type_name === 'phone_number') {
    return {
      kind: 'phone',
      commitTrigger: 'enter-or-blur',
      ownsEnterKey: false,
      autoOpenPicker: false,
      defaultOpen: false,
      overlayLayout: 'row',
    };
  }

  if (BARE_INPUT_FIELD_TYPES.has(propertyType.field_type_name)) {
    return {
      kind: 'bare-text',
      commitTrigger: 'enter-or-blur',
      ownsEnterKey: false,
      autoOpenPicker: false,
      defaultOpen: false,
      overlayLayout: 'row',
    };
  }

  return {
    kind: 'generic',
    commitTrigger: 'enter-or-blur',
    ownsEnterKey: false,
    autoOpenPicker: false,
    defaultOpen: false,
    overlayLayout: 'row',
  };
}

/**
 * True only for the multi-value PHONE card (editable-cell.tsx:385-386): its
 * rows are width-capped fields, so the card hugs their content instead of
 * stretching to the overlay's generic card-width floor.
 */
export function isPhoneCardEditor(
  descriptor: EditorKindDescriptor,
  propertyType?: PropertyTypeSchema
): boolean {
  return (
    descriptor.kind === 'multi-value-card' &&
    propertyType?.field_type_name === 'phone_number'
  );
}
